import cns from 'classnames'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useClickOutside } from '@/core/hooks/useClickOutside'
import { useScrollLock } from '@/core/hooks/useScrollLock'
import { useAppDispatch, useAppSelector } from '@/core/store'
import { closeModal } from '@/core/store/ui.store'

interface IModalProps {
  name: string
  children: React.ReactElement[] | React.ReactElement
  closeOnBackdropClick?: boolean
}

export const StrictModal: React.FC<IModalProps> = ({
  name,
  children,
  closeOnBackdropClick = false,
}) => {
  const { modal } = useAppSelector((store) => store.uiState)
  const dispatch = useAppDispatch()

  const modalRef = useRef<HTMLDivElement | null>(null)

  const { lockScroll, unlockScroll } = useScrollLock()

  const isActive = useMemo(() => {
    return modal === name
  }, [modal, name])

  const closeOnOutside = useCallback(() => {
    if (!closeOnBackdropClick || !isActive) return
    dispatch(closeModal())
  }, [closeOnBackdropClick, isActive, dispatch])

  useClickOutside(modalRef, closeOnOutside)

  useEffect(() => {
    if (isActive) {
      lockScroll()
    } else {
      unlockScroll()
    }

    return () => {
      unlockScroll()
    }
  }, [isActive])

  return (
    <>
      <div className={cns('modal-overlay', isActive && 'active')} />

      <div className={cns('modal', `modal--${name}`, isActive && 'active')} ref={modalRef}>
        <div className="modal-content">{children}</div>
      </div>
    </>
  )
}
